import { useMemo } from 'react'
import { MessageSquare, Compass, Sparkles, User } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { useHermesChat } from '@/hooks/useHermesChat'

type ActivityKind = 'user' | 'assistant' | 'context' | 'system'

interface ActivityEntry {
  id: string
  kind: ActivityKind
  label: string
  detail?: string
  createdAt?: string
}

function relativeTime(iso?: string): string {
  if (!iso) return ''
  const ts = Date.parse(iso)
  if (Number.isNaN(ts)) return ''
  const diff = Date.now() - ts
  if (diff < 60_000) return 'just now'
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m`
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h`
  return `${Math.floor(diff / 86_400_000)}d`
}

const KIND_META: Record<ActivityKind, { icon: LucideIcon; color: string }> = {
  user: { icon: User, color: 'var(--color-text-muted)' },
  assistant: { icon: Sparkles, color: '#d4a82a' },
  context: { icon: Compass, color: 'var(--color-text-muted)' },
  system: { icon: MessageSquare, color: 'var(--color-text-muted)' },
}

export function SkerpaActivityTab() {
  const { messages } = useHermesChat()

  const entries = useMemo<ActivityEntry[]>(() => {
    return messages.map(msg => {
      if (msg.content.startsWith('[CONTEXT_SWITCH:')) {
        const view = msg.content.match(/navigated to (.+?)\./)?.[1] || 'another view'
        return {
          id: msg.id,
          kind: 'context' as const,
          label: `Switched to ${view}`,
          createdAt: msg.createdAt,
        }
      }
      const text = msg.content.replace(/\[.*?\]/g, '').trim()
      const kind: ActivityKind = msg.role === 'user' ? 'user' : 'assistant'
      return {
        id: msg.id,
        kind,
        label: msg.role === 'user' ? 'You asked' : 'Skerpa replied',
        detail: text.length > 120 ? text.slice(0, 120) + '…' : text,
        createdAt: msg.createdAt,
      }
    }).reverse()
  }, [messages])

  if (entries.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center px-8 py-12 text-center">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-warm-100 mb-3">
          <MessageSquare className="h-5 w-5 text-text-muted" />
        </div>
        <div className="text-[13px] font-semibold text-text mb-1">No activity yet</div>
        <div className="text-[12px] text-text-muted max-w-[240px] leading-relaxed">
          Context switches, questions, and Skerpa replies will appear here as a timeline.
        </div>
      </div>
    )
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col gap-2">
      {entries.map(entry => {
        const meta = KIND_META[entry.kind]
        const Icon = meta.icon
        return (
          <div key={entry.id} className="flex gap-2.5 py-1.5">
            <div className="flex flex-col items-center flex-shrink-0 pt-0.5">
              <div
                className="flex h-6 w-6 items-center justify-center rounded-full"
                style={{ backgroundColor: entry.kind === 'assistant' ? '#fef9e7' : 'var(--color-warm-100)' }}
              >
                <Icon className="h-3 w-3" style={{ color: meta.color }} />
              </div>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-baseline gap-2">
                <span className="text-[12px] font-semibold text-text truncate">{entry.label}</span>
                <span className="text-[10px] text-text-muted flex-shrink-0">{relativeTime(entry.createdAt)}</span>
              </div>
              {entry.detail && (
                <div className="text-[12px] text-text-secondary leading-relaxed mt-0.5 line-clamp-2">
                  {entry.detail}
                </div>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}
